import { useEffect, useMemo, useRef, useState } from "react";
import type { SeatableReservation, SeatableReservations } from "../../../../../api/reservations";
import { useOnReservationsSourceChange } from "../../../../../hooks/useEdgeMode";
import { useNow } from "../../../../reservations/hooks/useNow";
import { seatOptionKey, seatOptionsFor, type SeatOption } from "./seatOptions";
import { groupByHour } from "./seatReservationGroups";

/**
 * Стъпката на модала: списъкът или въпросът „къде да настаним". `separateBills`
 * — „Отделна сметка за всяка маса" (само резервация с няколко маси; по
 * подразбиране „Една сметка").
 */
export type SeatStep =
  | { kind: "list" }
  | {
      kind: "choice";
      reservationId: number;
      options: SeatOption[];
      selectedKey: string | null;
      separateBills: boolean;
    };

/**
 * „Настани" отвън — гласовото „настани резервацията на Здравко", когато има
 * какво да се уточни: търсенето (казаното име) и резервацията, на която
 * модалът да отвори въпроса „къде да настаним" (`null` — само списъкът).
 */
export interface SeatModalInitial {
  query: string;
  reservationId: number | null;
}

/**
 * Състоянието на „Настани": търсене, избрана резервация (първата — най-ранната,
 * още при отваряне; „Настани" и без това минава през въпроса) и стъпката
 * „къде да настаним". Всичко се нулира при всяко отваряне — освен подаденото
 * `initial`, което се чете само при отварянето.
 */
export const useSeatModalState = ({
  open,
  data,
  initial = null,
}: {
  open: boolean;
  data: SeatableReservations | undefined;
  initial?: SeatModalInitial | null;
}) => {
  const nowMs = useNow();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [step, setStep] = useState<SeatStep>({ kind: "list" });
  // Резервацията от `initial`, чийто въпрос още чака данните.
  const [pendingChoiceId, setPendingChoiceId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Скрол до избрания САМО след ↑/↓ и автоматичния избор — не при клик
  // (иначе списъкът „подскача" под пръста).
  const scrollToSelectedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setQuery(initial?.query ?? "");
    setSelectedId(initial?.reservationId ?? null);
    setPendingChoiceId(initial?.reservationId ?? null);
    setStep({ kind: "list" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // `initial` с резервация: щом данните дойдат — направо въпросът „къде да
  // настаним" (там е за уточняване); спряна (рано, заето) — остава в списъка
  // с причината на изключения бутон.
  useEffect(() => {
    if (!open || !data || pendingChoiceId === null) return;
    setPendingChoiceId(null);
    const reservation = data.reservations.find((row) => row.id === pendingChoiceId);
    if (!reservation) return;
    const options = seatOptionsFor(reservation, data, nowMs);
    if (options.kind !== "choose") return;
    setStep({
      kind: "choice",
      reservationId: reservation.id,
      options: options.options,
      selectedKey: options.options.length === 1 ? seatOptionKey(options.options[0]) : null,
      separateBills: false,
    });
  }, [open, data, pendingChoiceId, nowMs]);

  // Смяна облак ↔ каса: въпросът „къде да настаним" е по данните на другата
  // страна — обратно към списъка, той се презарежда сам.
  useOnReservationsSourceChange(() => setStep({ kind: "list" }));

  const reservations = useMemo(() => data?.reservations ?? [], [data]);
  const groups = useMemo(() => groupByHour(reservations, query), [reservations, query]);
  const flat = useMemo(() => groups.flatMap((group) => group.reservations), [groups]);

  const selected: SeatableReservation | null = reservations.find((row) => row.id === selectedId) ?? null;

  // Избраната изчезна (настанена от друг терминал) или търсенето я скри → първата намерена.
  useEffect(() => {
    if (!open || !data || step.kind !== "list") return;
    if (selectedId !== null && flat.some((row) => row.id === selectedId)) return;
    scrollToSelectedRef.current = true;
    setSelectedId(flat[0]?.id ?? null);
  }, [open, data, step.kind, selectedId, flat]);

  // Резервацията от въпроса изчезна (настанена от друг терминал) → обратно към списъка.
  useEffect(() => {
    if (step.kind !== "choice" || !data) return;
    if (!data.reservations.some((row) => row.id === step.reservationId)) setStep({ kind: "list" });
  }, [step, data]);

  const seatOptions = selected && data ? seatOptionsFor(selected, data, nowMs) : null;

  const select = (reservation: SeatableReservation) => {
    setStep({ kind: "list" });
    setSelectedId(reservation.id);
  };

  /** ↑/↓ по намерените резервации (във въпроса — на родителя, по „Към коя сметка"). */
  const move = (delta: 1 | -1) => {
    if (flat.length === 0) return;
    const index = selectedId === null ? -1 : flat.findIndex((row) => row.id === selectedId);
    scrollToSelectedRef.current = true;
    setSelectedId(flat[Math.min(flat.length - 1, Math.max(0, index + delta))].id);
  };

  // Избраният ред остава видим при ↑/↓ и при автоматичния избор.
  useEffect(() => {
    if (selectedId === null || !scrollToSelectedRef.current) return;
    scrollToSelectedRef.current = false;
    document.querySelector(`[data-seat-row="${selectedId}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  return { nowMs, query, setQuery, groups, selected, select, move, seatOptions, step, setStep, inputRef };
};
